import { createSlice } from '@reduxjs/toolkit';
import {
  BasicSetupTypes,
  ConversationSetupTypes,
  GeneralTypes,
  KnowledgeBaseTypes,
  PDFType,
  SignalTypes,
  URLType,
  VIDEOType,
} from './types';
import {
  deleteWebsiteAgentKnowledgebase,
  getS3Url,
  getWebsiteAgentConfigDetails,
  getWebsiteConfigCompanyIndustry,
  reFetchWebsiteAgentConfigDetails,
  saveWebsiteAgentBasicSetUp,
  saveWebsiteAgentConversationSetup,
  saveWebsiteAgentGeneral,
  saveWebsiteAgentKnowledgebase,
  saveWebsiteAgentSproutsSignals,
} from './website-agent-config.actions';
import { defaultData, mappings } from './constants';

export interface InitialState {
  getLoader: boolean;
  saveLoader: boolean;
  general: GeneralTypes;
  basicSetup: BasicSetupTypes;
  sproutsSignals: SignalTypes;
  knowledgeBase: KnowledgeBaseTypes;
  conversationSetup: ConversationSetupTypes;
  currentStep: number;
  deletedKnowledgeBase: KnowledgeBaseTypes;
}

const initialState: InitialState = {
  getLoader: false,
  saveLoader: false,
  general: {
    aiName: '',
    intro: '',
  },
  basicSetup: {
    companyUrl: '',
    businessCategoryOptions: [],
    businessCategory: [],
    companyDescription: '',
    emailId: '',
  },
  sproutsSignals: {
    CUSTOM_AI_SIGNAL: [],
  },
  knowledgeBase: {
    URLS: [],
    PDFS: [],
    PPTS: [],
    VIDEOS: [],
  },
  conversationSetup: {
    data: [],
  },
  deletedKnowledgeBase: {
    URLS: [],
    PDFS: [],
    PPTS: [],
    VIDEOS: [],
  },
  currentStep: 0,
};

const websiteAgentConfigSlice = createSlice({
  name: 'website-agent-config',
  initialState,
  reducers: {
    setAIName: (state, action) => {
      state.general.aiName = action.payload;
    },
    setIntro: (state, action) => {
      state.general.intro = action.payload;
    },
    setCompanyUrl: (state, action) => {
      state.basicSetup.companyUrl = action.payload;
    },
    setCompanyDescription: (state, action) => {
      state.basicSetup.companyDescription = action.payload;
    },
    setEmail: (state, action) => {
      state.basicSetup.emailId = action.payload;
    },
    setCompanyIndustry: (state, action) => {
      state.basicSetup.businessCategory = action.payload;
    },
    setCompanyIndustryOptions: (state, action) => {
      state.basicSetup.businessCategoryOptions = Array.isArray(action.payload)
        ? action.payload.filter((item) => typeof item !== 'undefined' && item !== null)
        : [];
    },
    setAddNewUrl: (state, action) => {
      state.knowledgeBase.URLS = [...state.knowledgeBase.URLS, action.payload];
    },
    setEditUrl: (state, action) => {
      const exUrls = [...state.knowledgeBase.URLS];
      state.knowledgeBase.URLS = exUrls.map((item: URLType, idx: number) => {
        if (idx === action.payload.idx) {
          return action.payload.urlData;
        } else {
          return item;
        }
      });
    },
    setAddNewPDF: (state, action) => {
      state.knowledgeBase.PDFS = [...state.knowledgeBase.PDFS, action.payload];
    },
    setEditPDF: (state, action) => {
      const exPdfs = [...state.knowledgeBase.PDFS];
      state.knowledgeBase.PDFS = exPdfs.map((item: PDFType, idx: number) => {
        if (idx === action.payload.idx) {
          return action.payload.pdfData;
        } else {
          return item;
        }
      });
    },
    setAddNewPPT: (state, action) => {
      state.knowledgeBase.PPTS = [...state.knowledgeBase.PPTS, action.payload];
    },
    setEditPPT: (state, action) => {
      const exPpts = [...state.knowledgeBase.PPTS];
      state.knowledgeBase.PPTS = exPpts.map((item: PDFType, idx: number) => {
        if (idx === action.payload.idx) {
          return action.payload.pptData;
        } else {
          return item;
        }
      });
    },
    setAddNewVideo: (state, action) => {
      state.knowledgeBase.VIDEOS = [...(state.knowledgeBase.VIDEOS || []), action.payload];
    },
    setEditVideo: (state, action) => {
      const exVideos = [...state.knowledgeBase.VIDEOS];
      state.knowledgeBase.VIDEOS = exVideos.map((item: VIDEOType, idx: number) => {
        if (idx === action.payload.idx) {
          return action.payload.videoData;
        } else {
          return item;
        }
      });
    },
    setEditConversation: (state, action) => {
      const setup = JSON.stringify(action.payload);
      state.conversationSetup.data = JSON.parse(setup);
    },
    setToggleSignal: (state, action) => {
      const signal = JSON.stringify(action.payload);
      state.sproutsSignals.CUSTOM_AI_SIGNAL = JSON.parse(signal);
    },
    setDeleteUrl: (state, action) => {
      if (action.payload.primaryKey) {
        state.deletedKnowledgeBase.primaryKey = state.knowledgeBase.primaryKey;
        state.deletedKnowledgeBase.URLS = [...state.deletedKnowledgeBase.URLS, action.payload];
        state.knowledgeBase.URLS = state.knowledgeBase.URLS.filter(
          (item) => item.primaryKey !== action.payload.primaryKey,
        );
      } else {
        state.knowledgeBase.URLS = state.knowledgeBase.URLS.filter(
          (item, idx) => idx !== action.payload.idx,
        );
      }
    },
    setDeletePDF: (state, action) => {
      if (action.payload.primaryKey) {
        state.deletedKnowledgeBase.primaryKey = state.knowledgeBase.primaryKey;
        state.deletedKnowledgeBase.PDFS = [...state.deletedKnowledgeBase.PDFS, action.payload];
        state.knowledgeBase.PDFS = state.knowledgeBase.PDFS.filter(
          (item) => item.primaryKey !== action.payload.primaryKey,
        );
      } else {
        state.knowledgeBase.PDFS = state.knowledgeBase.PDFS.filter(
          (item, idx) => idx !== action.payload.idx,
        );
      }
    },
    setDeletePPT: (state, action) => {
      if (action.payload.primaryKey) {
        state.deletedKnowledgeBase.primaryKey = state.knowledgeBase.primaryKey;
        state.deletedKnowledgeBase.PPTS = [...state.deletedKnowledgeBase.PPTS, action.payload];
        state.knowledgeBase.PPTS = state.knowledgeBase.PPTS.filter(
          (item) => item.primaryKey !== action.payload.primaryKey,
        );
      } else {
        state.knowledgeBase.PPTS = state.knowledgeBase.PPTS.filter(
          (item, idx) => idx !== action.payload.idx,
        );
      }
    },
    setDeleteVIDEO: (state, action) => {
      if (action.payload.primaryKey) {
        state.deletedKnowledgeBase.primaryKey = state.knowledgeBase.primaryKey;
        state.deletedKnowledgeBase.VIDEOS = [...state.deletedKnowledgeBase.VIDEOS, action.payload];
        state.knowledgeBase.VIDEOS = state.knowledgeBase.VIDEOS.filter(
          (item) => item.primaryKey !== action.payload.primaryKey,
        );
      } else {
        state.knowledgeBase.VIDEOS = state.knowledgeBase.VIDEOS.filter(
          (item, idx) => idx !== action.payload.idx,
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWebsiteAgentConfigDetails.pending, (state) => {
      state.getLoader = true;
      state.deletedKnowledgeBase = {
        URLS: [],
        PDFS: [],
        PPTS: [],
        VIDEOS: [],
      };
    });
    builder.addCase(getWebsiteAgentConfigDetails.fulfilled, (state, action) => {
      state.currentStep = Object.keys(action.payload).length || 0;
      Object.entries(mappings).forEach(([key, stateKey]) => {
        state[stateKey] = action.payload[key] ?? defaultData[key];
      });
      state.deletedKnowledgeBase = {
        URLS: [],
        PDFS: [],
        PPTS: [],
        VIDEOS: [],
      };
      state.getLoader = false;
    });
    builder.addCase(getWebsiteAgentConfigDetails.rejected, (state) => {
      state.getLoader = false;
      state.deletedKnowledgeBase = {
        URLS: [],
        PDFS: [],
        PPTS: [],
        VIDEOS: [],
      };
    });
    builder.addCase(reFetchWebsiteAgentConfigDetails.pending, (state) => {
      state.deletedKnowledgeBase = {
        URLS: [],
        PDFS: [],
        PPTS: [],
        VIDEOS: [],
      };
    });
    builder.addCase(reFetchWebsiteAgentConfigDetails.fulfilled, (state, action) => {
      state.currentStep = Object.keys(action.payload).length || 0;
      Object.entries(mappings).forEach(([key, stateKey]) => {
        state[stateKey] = action.payload[key] ?? defaultData[key];
      });
      state.deletedKnowledgeBase = {
        URLS: [],
        PDFS: [],
        PPTS: [],
        VIDEOS: [],
      };
    });
    builder.addCase(reFetchWebsiteAgentConfigDetails.rejected, (state) => {
      state.deletedKnowledgeBase = {
        URLS: [],
        PDFS: [],
        PPTS: [],
        VIDEOS: [],
      };
    });
    builder.addCase(saveWebsiteAgentGeneral.pending, (state) => {
      state.saveLoader = true;
    });
    builder.addCase(saveWebsiteAgentGeneral.fulfilled, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(saveWebsiteAgentGeneral.rejected, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(saveWebsiteAgentBasicSetUp.pending, (state) => {
      state.saveLoader = true;
    });
    builder.addCase(saveWebsiteAgentBasicSetUp.fulfilled, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(saveWebsiteAgentBasicSetUp.rejected, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(saveWebsiteAgentSproutsSignals.pending, (state) => {
      state.saveLoader = true;
    });
    builder.addCase(saveWebsiteAgentSproutsSignals.fulfilled, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(saveWebsiteAgentSproutsSignals.rejected, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(saveWebsiteAgentKnowledgebase.pending, (state) => {
      state.saveLoader = true;
    });
    builder.addCase(saveWebsiteAgentKnowledgebase.fulfilled, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(saveWebsiteAgentKnowledgebase.rejected, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(deleteWebsiteAgentKnowledgebase.pending, (state) => {
      state.saveLoader = true;
    });
    builder.addCase(deleteWebsiteAgentKnowledgebase.fulfilled, (state) => {
      // state.saveLoader = false;
    });
    builder.addCase(deleteWebsiteAgentKnowledgebase.rejected, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(saveWebsiteAgentConversationSetup.pending, (state) => {
      state.saveLoader = true;
    });
    builder.addCase(saveWebsiteAgentConversationSetup.fulfilled, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(saveWebsiteAgentConversationSetup.rejected, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(getWebsiteConfigCompanyIndustry.pending, (state) => {
      state.saveLoader = true;
    });
    builder.addCase(getWebsiteConfigCompanyIndustry.fulfilled, (state, action) => {
      state.basicSetup.businessCategoryOptions = Array.isArray(action.payload)
        ? action.payload.filter((item) => typeof item !== 'undefined' && item !== null)
        : [];
      state.saveLoader = false;
    });
    builder.addCase(getWebsiteConfigCompanyIndustry.rejected, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(getS3Url.pending, (state) => {
      state.saveLoader = true;
    });
    builder.addCase(getS3Url.fulfilled, (state) => {
      state.saveLoader = false;
    });
    builder.addCase(getS3Url.rejected, (state) => {
      state.saveLoader = false;
    });
  },
});

export const {
  setAIName,
  setIntro,
  setCompanyUrl,
  setCompanyDescription,
  setEmail,
  setCompanyIndustry,
  setCompanyIndustryOptions,
  setAddNewUrl,
  setEditUrl,
  setAddNewPDF,
  setEditPDF,
  setAddNewPPT,
  setEditPPT,
  setAddNewVideo,
  setEditVideo,
  setEditConversation,
  setToggleSignal,
  setDeletePDF,
  setDeletePPT,
  setDeleteUrl,
  setDeleteVIDEO,
} = websiteAgentConfigSlice.actions;

export default websiteAgentConfigSlice.reducer;
